/**
 * character-arc-history (= narrative-quality-future-enhancements 推奨順位 2) Phase 3:
 * client-player から character arc transition log API を呼び出すための軽量 fetch wrapper。
 *
 * 設計 doctrine:
 * - client-player は core-domain への dep を持たない (= memory
 *   `project_client_player_no_core_domain_dep`)、 types は本 file 内で独自 declaration
 * - sibling: beats.ts と同 pattern (= manual registration endpoint、 openapi-fetch 外)
 *
 * 設計対応:
 *   - .kiro/specs/character-arc-history/brief.md (= Option A Phase 3)
 */
import { ApiError, type ProblemDetail } from "./client";

// ─── 独自 type declaration (= core-domain dep なし) ─────────

export interface CharacterArcHistoryEntry {
  id: string;
  scenarioId: string;
  characterId: string;
  fromArcText: string | null;
  toArcText: string;
  triggerSceneId: string | null;
  triggerPlaylineId: string | null;
  transitionedAt: string;
}

export interface CharacterArcHistoryResponse {
  entries: CharacterArcHistoryEntry[];
}

function fallbackProblem(status: number, statusText: string): ProblemDetail {
  return {
    type: "about:blank",
    title: "Request failed",
    status,
    detail: statusText !== "" ? statusText : "Unknown error",
  };
}

async function parseProblem(response: Response): Promise<ProblemDetail> {
  try {
    const json = (await response.json()) as ProblemDetail;
    if (typeof json.type === "string" && typeof json.title === "string") {
      return json;
    }
  } catch {
    /* JSON parse 失敗時は fallback */
  }
  return fallbackProblem(response.status, response.statusText);
}

/**
 * GET /api/v1/scenarios/:scenarioId/characters/:characterId/arc/history
 *
 * timeline 降順で audit log entries を返す。 limit (= default 20、 0/負数で全件) は query
 * param 経由。
 */
export async function listCharacterArcHistory(
  baseUrl: string,
  scenarioId: string,
  characterId: string,
  limit?: number,
): Promise<CharacterArcHistoryResponse> {
  const qs =
    limit !== undefined ? `?limit=${encodeURIComponent(String(limit))}` : "";
  const response = await fetch(
    `${baseUrl}/scenarios/${encodeURIComponent(scenarioId)}/characters/${encodeURIComponent(characterId)}/arc/history${qs}`,
    { method: "GET", headers: { Accept: "application/json" } },
  );
  if (!response.ok) {
    throw new ApiError(response.status, await parseProblem(response));
  }
  return (await response.json()) as CharacterArcHistoryResponse;
}
